import { cornerRadius } from "@bug-on/m3-tokens";
import type { Transition } from "motion";
import {
	FAST_EFFECTS_SPRING,
	FAST_SPATIAL_SPRING,
} from "../../shared/motion-tokens";

/**
 * Shape morphing spring animation for layout/width morphing.
 * MD3: `fast.spatial` — stiffness 800, dampingRatio 0.6 (has bounce).
 */
export const BUTTON_SHAPE_MORPH_SPRING: Transition = FAST_SPATIAL_SPRING;

/**
 * Spring for border-radius changes on press/release and shape toggle.
 * MD3: `fast.effects` — stiffness 3800, critically damped (dampingRatio 1.0).
 * No overshoot; border-radius never goes negative.
 */
export const BUTTON_RADIUS_SPRING: Transition = FAST_EFFECTS_SPRING;

export const ROUND_SHAPE_RADIUS = 9999;

export const BUTTON_SIZE_TOKENS = {
	xs: {
		height: "2rem", // 32dp
		leadingSpace: "1rem", // 16dp
		trailingSpace: "1rem", // 16dp
		iconSize: "1.25rem", // 20dp
		iconLabelSpace: "0.5rem", // 8dp
		outlineWidth: 1, // 1dp
		squareShapeRadius: cornerRadius.medium, // 12dp
		pressedShapeRadius: cornerRadius.small, // 8dp
		roundShapeRadius: ROUND_SHAPE_RADIUS,
	},
	sm: {
		height: "2.5rem", // 40dp
		leadingSpace: "1rem", // 16dp
		trailingSpace: "1rem", // 16dp
		iconSize: "1.25rem", // 20dp
		iconLabelSpace: "0.5rem", // 8dp
		outlineWidth: 1, // 1dp
		squareShapeRadius: cornerRadius.medium, // 12dp
		pressedShapeRadius: cornerRadius.small, // 8dp
		roundShapeRadius: ROUND_SHAPE_RADIUS,
	},
	md: {
		height: "3.5rem", // 56dp
		leadingSpace: "1.5rem", // 24dp
		trailingSpace: "1.5rem", // 24dp
		iconSize: "1.5rem", // 24dp
		iconLabelSpace: "0.5rem", // 8dp
		outlineWidth: 1, // 1dp
		squareShapeRadius: cornerRadius.large, // 16dp
		pressedShapeRadius: cornerRadius.medium, // 12dp
		roundShapeRadius: ROUND_SHAPE_RADIUS,
	},
	lg: {
		height: "6rem", // 96dp
		leadingSpace: "3rem", // 48dp
		trailingSpace: "3rem", // 48dp
		iconSize: "2rem", // 32dp
		iconLabelSpace: "0.75rem", // 12dp
		outlineWidth: 2, // 2dp
		squareShapeRadius: cornerRadius.extraLarge, // 28dp
		pressedShapeRadius: cornerRadius.large, // 16dp
		roundShapeRadius: ROUND_SHAPE_RADIUS,
	},
	xl: {
		height: "8.5rem", // 136dp
		leadingSpace: "4rem", // 64dp
		trailingSpace: "4rem", // 64dp
		iconSize: "2.5rem", // 40dp
		iconLabelSpace: "1rem", // 16dp
		outlineWidth: 3, // 3dp
		squareShapeRadius: cornerRadius.extraLarge, // 28dp
		pressedShapeRadius: cornerRadius.large, // 16dp
		roundShapeRadius: ROUND_SHAPE_RADIUS,
	},
} as const;

export const BUTTON_TYPOGRAPHY_TOKENS = {
	xs: "text-sm font-medium tracking-wide", // labelLarge
	sm: "text-sm font-medium tracking-wide", // labelLarge
	md: "text-base font-medium", // titleMedium
	lg: "text-xl font-normal", // headlineSmall
	xl: "text-[2rem] font-normal leading-none", // headlineLarge
} as const;

export const BUTTON_COLOR_TOKENS = {
	elevated: {
		default: "bg-m3-surface-container-low text-m3-primary shadow-md",
		selected: "bg-m3-primary text-m3-on-primary shadow-md",
		unselected: "bg-m3-surface-container-low text-m3-primary shadow-md",
	},
	filled: {
		default: "bg-m3-primary text-m3-on-primary",
		selected: "bg-m3-primary text-m3-on-primary",
		unselected: "bg-m3-surface-container text-m3-on-surface-variant",
	},
	tonal: {
		default: "bg-m3-secondary-container text-m3-on-secondary-container",
		selected: "bg-m3-secondary text-m3-on-secondary",
		unselected: "bg-m3-secondary-container text-m3-on-secondary-container",
	},
	outlined: {
		default:
			"bg-transparent text-m3-on-surface-variant border-m3-outline-variant",
		selected:
			"bg-m3-inverse-surface text-m3-inverse-on-surface border-transparent",
		unselected:
			"bg-transparent text-m3-on-surface-variant border-m3-outline-variant",
	},
	text: {
		default: "bg-transparent text-m3-primary",
		selected: "bg-transparent text-m3-primary",
		unselected: "bg-transparent text-m3-primary",
	},

	// ── MD3 Expressive accent variants ──────────────────────────────────────
	/**
	 * High-chroma tertiary tone. Use for hero CTAs and expressive moments where
	 * a distinct accent beyond primary is needed (e.g., hero chip, highlight fab).
	 */
	tertiary: {
		default: "bg-m3-tertiary text-m3-on-tertiary",
		selected: "bg-m3-tertiary text-m3-on-tertiary",
		unselected: "bg-m3-tertiary-container text-m3-on-tertiary-container",
	},

	/**
	 * Brand-stable primary fixed. Hue stays consistent across light and dark modes.
	 * Ideal for badges, pill chips, and elements that must carry the brand color
	 * regardless of the active theme.
	 */
	"primary-fixed": {
		default: "bg-m3-primary-fixed text-m3-on-primary-fixed",
		selected: "bg-m3-primary-fixed text-m3-on-primary-fixed",
		unselected: "bg-m3-primary-fixed text-m3-on-primary-fixed-variant",
	},

	/**
	 * Brand-stable tertiary fixed. Use for special-state indicators, achievement
	 * badges, or accent pills that need a fixed tertiary hue across themes.
	 */
	"tertiary-fixed": {
		default: "bg-m3-tertiary-fixed text-m3-on-tertiary-fixed",
		selected: "bg-m3-tertiary-fixed text-m3-on-tertiary-fixed",
		unselected: "bg-m3-tertiary-fixed text-m3-on-tertiary-fixed-variant",
	},
} as const;
